import type { LedgerSubject, NotFoundReason } from '@schemas/error-pages'
import { NOT_FOUND_HEADLINE_BY_REASON } from '@schemas/error-pages'
import { m } from 'motion/react'
import { fadeSlideUp } from '../../lib/motion.ts'
import { Badge } from '../badge.tsx'
import { Button } from '../button.tsx'
import { Card, CardBody } from '../card.tsx'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
})

// RAL-162 board 1a: what stopped a creator re-uploading a file they deliberately removed
// is naming the record that outlives it, not apologizing for its absence.
export function LedgerLayout({ reason, subject }: { reason: NotFoundReason; subject: LedgerSubject }) {
  return (
    <m.div variants={fadeSlideUp} initial="hidden" animate="visible" className="mx-auto max-w-xl">
      <Card className="shadow-[8px_8px_0_var(--color-shadow)]">
        <CardBody className="flex flex-col gap-5 px-8 py-9">
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge variant="neutral">Not here any more</Badge>
            <span className="font-mono text-[10px] text-ink-secondary">{subject.path}</span>
          </div>

          <h1 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink-primary">
            {NOT_FOUND_HEADLINE_BY_REASON[reason]}
          </h1>

          <p className="max-w-prose text-sm leading-relaxed text-ink-soft">
            You deleted <strong className="font-semibold text-ink-primary">"{subject.objectLabel}"</strong> on{' '}
            {dateFormatter.format(subject.deletedAt)}. Rally doesn't keep clips after you remove them.
          </p>

          <div className="border border-border-hairline bg-ground-inset px-4.5 py-4">
            <p className="mb-3 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-ink-secondary">
              What survived it
            </p>
            <div className="flex flex-col gap-2.5 text-xs">
              {subject.publishedRecord ? (
                <div className="flex gap-3">
                  <span aria-hidden="true" className="shrink-0 font-mono text-signal-ok">
                    ✓
                  </span>
                  <span className="text-ink-soft">
                    Its <strong className="font-semibold text-ink-primary">published record</strong> —{' '}
                    {subject.publishedRecord.postCount} post{subject.publishedRecord.postCount === 1 ? '' : 's'},{' '}
                    {dateFormatter.format(subject.publishedRecord.publishedAt)}, {subject.publishedRecord.views} views
                  </span>
                </div>
              ) : null}
              {subject.hasActivityRows ? (
                <div className="flex gap-3">
                  <span aria-hidden="true" className="shrink-0 font-mono text-signal-ok">
                    ✓
                  </span>
                  <span className="text-ink-soft">
                    Its rows in <strong className="font-semibold text-ink-primary">Activity</strong> — the record of a
                    decision outlives the thing it was about
                  </span>
                </div>
              ) : null}
              <div className="flex gap-3">
                <span aria-hidden="true" className="shrink-0 font-mono text-ink-secondary">
                  ×
                </span>
                <span className="text-ink-muted">{subject.lostDetail}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-5 border-t border-border-hairline pt-4">
            {subject.publishedRecord ? (
              <Button as="a" href="/published" variant="secondary" size="sm">
                See its published post
              </Button>
            ) : null}
            <Button as="a" href="/" variant="secondary" size="sm">
              Back to library
            </Button>
            <span className="font-mono text-[11px] text-ink-secondary">Nothing about your campaign changed.</span>
          </div>
        </CardBody>
      </Card>
    </m.div>
  )
}
